import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import axios from 'axios';

import {
    FaUserPlus,
    FaPlay,
    FaBackward,
    FaForward,
    FaPause,
    FaCheckCircle,
    FaMapMarkerAlt,
    FaParking,
    FaTimes,
    FaArrowLeft,
    FaArrowRight,
    FaTag, // Added
    FaLanguage, // Added
    FaCalendarAlt, // Added
    FaClock, // Added
} from "react-icons/fa";
import "./Invitation.css";

const youTubeScriptUrl = "https://www.youtube.com/iframe_api";

// MOCK DATA (to be replaced by API call)

function Invitation() {
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    // Déterminez l'étape initiale en fonction de l'URL
    const isThankYouPage = location.pathname.includes("/merci-formation");
    const [step, setStep] = useState(isThankYouPage ? 3 : 1);
    const [formData, setFormData] = useState({
        lastName: "",
        firstName: "",
        email: "",
        country: "",
        city: "",
        address: "",
        phoneNumber: "",
        session: [], // Will store session IDs
    });
    const [selectedDates, setSelectedDates] = useState({}); // Will store { sessionId: [date1, date2] }

    useEffect(() => {
        const isMerciPage = location.pathname.includes("/merci-formation");
        if (isMerciPage) {
            setStep(3);
        }
    }, [location.pathname]);

    const [showCountryModal, setShowCountryModal] = useState(false);
    const [showCityModal, setShowCityModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [loading, setLoading] = useState(false); // Add this line

    const [sessions, setSessions] = useState([]); // Added
    const [countries, setCountries] = useState([]); // Added
    const [cities, setCities] = useState([]); // Added

    const [playing, setPlaying] = useState(false);
    const iframeRefStep1 = useRef(null);
    const iframeRefStep3 = useRef(null);
    const playerRef = useRef(null);
    const [isYoutubeApiLoaded, setIsYoutubeApiLoaded] = useState(false);

   const videoMap = {
       fr: "Z8sPke5cZ9I",
       en: "QgvKdgJAJPI",
   };

    useEffect(() => {
        if (!window.YT) {
            const tag = document.createElement("script");
            tag.src = youTubeScriptUrl;
            const firstScriptTag = document.getElementsByTagName("script")[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
            window.onYouTubeIframeAPIReady = () => setIsYoutubeApiLoaded(true);
        } else {
            setIsYoutubeApiLoaded(true);
        }
    }, []);

    useEffect(() => {
        let currentIframeRef =
            step === 1 ? iframeRefStep1.current : iframeRefStep3.current;
        if (isYoutubeApiLoaded && currentIframeRef) {
            const videoId = videoMap[i18n.language] || videoMap.fr;
            if (playerRef.current) {
                playerRef.current.loadVideoById(videoId);
            } else {
                playerRef.current = new window.YT.Player(currentIframeRef, {
                    videoId: videoId,
                    events: {
                        onReady: (event) =>
                            console.log("YouTube Player est prêt !"),
                        onStateChange: (event) =>
                            setPlaying(
                                event.data === window.YT.PlayerState.PLAYING
                            ),
                    },
                    playerVars: {
                        controls: 0,
                        modestbranding: 1,
                        rel: 0,
                        autoplay: 0,
                        showinfo: 0,
                        disablekb: 1,
                    },
                });
            }
            setPlaying(false);
        }
        return () => {
            if (playerRef.current) {
                playerRef.current.destroy();
                playerRef.current = null;
            }
        };
    }, [i18n.language, isYoutubeApiLoaded, step]);

    useEffect(() => {
        const fetchSessions = async () => {
            if (formData.cityId) {
                // Only fetch sessions if a city is selected
                try {
                    setLoading(true);
                    const response = await fetch(
                        `https://back-desalpe.onrender.com/api/sessions/city/${formData.cityId}`
                    ); // Use listSessionsForCity endpoint
                    if (!response.ok) {
                        throw new Error(
                            `HTTP error! status: ${response.status}`
                        );
                    }
                    const data = await response.json();
                    setSessions(data); // setSessions with filtered data from backend
                } catch (error) {
                    console.error("Error fetching sessions:", error);
                    setErrorMessage("Erreur lors du chargement des sessions.");
                    setShowErrorModal(true);
                } finally {
                    setLoading(false);
                }
            } else {
                setSessions([]); // Clear sessions if no city is selected
            }
        };
        fetchSessions();
    }, [formData.cityId]); // Re-run when selected city changes

    const handlePlayPause = () => {
        if (playerRef.current) {
            playing
                ? playerRef.current.pauseVideo()
                : playerRef.current.playVideo();
        }
    };

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                setLoading(true);
                const response = await fetch(
                    "https://back-desalpe.onrender.com/api/countries"
                );
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setCountries(data);
            } catch (error) {
                console.error("Error fetching countries:", error);
                setErrorMessage("Erreur lors du chargement des pays.");
                setShowErrorModal(true);
            } finally {
                setLoading(false);
            }
        };
        fetchCountries();
    }, []); // Run once on mount

    const handleRewind = () =>
        playerRef.current?.seekTo(
            playerRef.current.getCurrentTime() - 10,
            true
        );
    useEffect(() => {
        const fetchCities = async () => {
            if (formData.country) {
                // Fetch cities only if a country is selected
                try {
                    setLoading(true);
                    // Assuming backend expects country name, not ID, for fetching cities
                    const response = await fetch(
                        `https://back-desalpe.onrender.com/api/cities/country/${formData.countryId}`
                    ); // Using countryId
                    if (!response.ok) {
                        throw new Error(
                            `HTTP error! status: ${response.status}`
                        );
                    }
                    const data = await response.json();
                    setCities(data);
                } catch (error) {
                    console.error("Error fetching cities:", error);
                    setErrorMessage("Erreur lors du chargement des villes.");
                    setShowErrorModal(true);
                } finally {
                    setLoading(false);
                }
            } else {
                setCities([]); // Clear cities if no country is selected
            }
        };
        fetchCities();
    }, [formData.country]); // Re-run when selected country changes

    const handleFastForward = () =>
        playerRef.current?.seekTo(
            playerRef.current.getCurrentTime() + 10,
            true
        );

    const handleSessionChange = (e) => {
        const { value, checked } = e.target;
        const newSessions = checked
            ? [...formData.session, value]
            : formData.session.filter((id) => id !== value);
        if (!checked) {
            const newSelectedDates = { ...selectedDates };
            delete newSelectedDates[value];
            setSelectedDates(newSelectedDates);
        }
        setFormData({ ...formData, session: newSessions });
    };

    const handleDateChange = (sessionId, date) => {
        const newSelectedDates = { ...selectedDates };
        if (!newSelectedDates[sessionId]) newSelectedDates[sessionId] = [];

        if (newSelectedDates[sessionId].includes(date)) {
            newSelectedDates[sessionId] = newSelectedDates[sessionId].filter(
                (d) => d !== date
            );
        } else {
            newSelectedDates[sessionId].push(date);
        }
        setSelectedDates(newSelectedDates);
    };

    const handleInputChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });
    const handleCountrySelect = (countryId, countryName) => {
        setFormData({
            ...formData,
            countryId: countryId,
            country: countryName,
            cityId: "",
            city: "",
        }); // Store ID and Name
        setShowCountryModal(false);
    };
    const handleCitySelect = (cityId, cityName) => {
        // Now accepts ID and Name
        setFormData({ ...formData, cityId: cityId, city: cityName }); // Store ID and Name
        setShowCityModal(false);
    };

    const handleFormSubmit = async (e) => {
        // Make it async
        e.preventDefault();
        const requiredFields = [
            "lastName",
            "firstName",
            "email",
            "countryId",
            "cityId",
        ];
        if (formData.session.length === 0) {
            // Using t() function for translation
            setErrorMessage(t("invitation.error_select_session"));
            setShowErrorModal(true);
            return;
        }
        for (let field of requiredFields) {
            if (!formData[field]) {
                // Using t() function for translation
                setErrorMessage(t("invitation.error_fill_required_fields"));
                setShowErrorModal(true);
                return;
            }
        }

        setLoading(true);
        setErrorMessage("");

        try {
            // Step 1: Check for existing registration
            const checkPayload = {
                email: formData.email,
                sessionIds: formData.session,
            };

            const checkResponse = await fetch(
                "https://back-desalpe.onrender.com/api/invitations/check-registration",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(checkPayload),
                }
            );
            const checkData = await checkResponse.json();

            if (!checkResponse.ok) {
                throw new Error(
                    checkData.message ||
                    t("invitation.error_verification_failed")
                );
            }

            // Check for the messageKey from the backend
            if (
                checkData.exists &&
                checkData.messageKey === "ERROR_ALREADY_REGISTERED"
            ) {
                setErrorMessage(t("invitation.error_already_registered"));
                setShowErrorModal(true);
                setLoading(false);
                return;
            }

            // Clear any previous error messages before attempting to create the invitation
            setErrorMessage("");
            setShowErrorModal(false);

            // Step 2: Proceed with creating the invitation
            const invitationData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                countryId: formData.countryId,
                cityId: formData.cityId,
                address: formData.address,
                sessionIds: formData.session,
                selectedSessionDates: selectedDates,
            };

            const createResponse = await fetch(
                "https://back-desalpe.onrender.com/api/invitations",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(invitationData),
                }
            );

            if (!createResponse.ok) {
                const errorData = await createResponse.json();
                throw new Error(
                    errorData.message ||
                    `HTTP error! status: ${createResponse.status}`
                );
            }

            const result = await createResponse.json();
            console.log("Registration successful:", result);
            console.log("Attempting to navigate to /merci-formation...");
            navigate("/merci-formation");
            console.log("Navigation call completed.");
        } catch (err) {
            console.error("Error during registration:", err);
            setErrorMessage(
                t("invitation.error_generic_submission_failed", {
                    message: err.message,
                })
            );
            setShowErrorModal(true);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmPresence = async () => {
            console.log("--- Invitation.js: Confirming presence ---");
            setLoading(true);
            try {
                const response = await fetch(
                    "https://back-desalpe.onrender.com/api/anonymous-clicks/invitation-confirmation",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            timestamp: new Date().toISOString(),
                            status: "confirmed", // You can customize this status
                        }),
                    }
                );

                const responseData = await response.json();

                if (response.ok) {
                    console.log(
                        "--- Invitation.js: Presence confirmation successful ---",
                        responseData
                    );
                    setShowSuccessModal(true);
                } else {
                    console.error(
                        "--- Invitation.js: Presence confirmation failed ---",
                        responseData
                    );
                    setErrorMessage(responseData.message || "An error occurred.");
                    setShowErrorModal(true);
                }
            } catch (error) {
                console.error("--- Invitation.js: Fetch failed ---", error);
                setErrorMessage("Network error. Please try again.");
                setShowErrorModal(true);
            } finally {
                setLoading(false);
            }
        };
        const handleSuccessModalClose = () => {
            setShowSuccessModal(false);
            setStep(1);
            setFormData({
                lastName: "",
                firstName: "",
                email: "",
                country: "",
                city: "",
                address: "",
                phoneNumber: "",
                session: [],
            });
            setSelectedDates({});
        };

        return (
            <div className="invitation-page-container">
                <div className="invitation-steps-indicator">
                    <div className={`step-circle ${step >= 1 ? "active" : ""}`}>
                        1
                    </div>
                    <div className={`step-circle ${step >= 2 ? "active" : ""}`}>
                        2
                    </div>
                    <div className={`step-circle ${step >= 3 ? "active" : ""}`}>
                        3
                    </div>
                </div>
                <div className="invitation-content">
                    {step === 1 && (
                        <div className="invitation-step step-1">
                            <h1>{t("invitation.step1_title")}</h1>
                            <p className="invitation-text-summary">
                                {t("invitation.step1_summary")}
                            </p>
                            <div className="invitation-video-player-area">
                                <div className="invitation-player-wrapper">
                                    <div className="video-overlay"></div>
                                    <div
                                        id="invitation-youtube-player"
                                        ref={iframeRefStep1}
                                    ></div>
                                </div>
                                <p className="invitation-video-instruction">
                                    {t("invitation.click_to_play")}
                                </p>
                                <div className="video-controls">
                                    <button
                                        onClick={handleRewind}
                                        className="video-control-button"
                                    >
                                        <FaBackward />
                                    </button>
                                    <button
                                        onClick={handlePlayPause}
                                        className="play-pause-button"
                                    >
                                        {playing ? <FaPause /> : <FaPlay />}
                                    </button>
                                    <button
                                        onClick={handleFastForward}
                                        className="video-control-button"
                                    >
                                        <FaForward />
                                    </button>
                                </div>
                            </div>
                            <div className="invitation-location-details">
                                <p className="directions">
                                    {t("invitation.step1_direction")}
                                </p>
                                <p className="location">
                                    <FaMapMarkerAlt />{" "}
                                    {t("invitation.step1_location_address")}
                                </p>
                                <p className="location">
                                    <FaParking />{" "}
                                    {t("invitation.step1_location_parking")}
                                </p>
                            </div>
                            <div className="button-center-container">
                                <button
                                    onClick={() => setStep(2)}
                                    className="invitation-button"
                                >
                                    <FaUserPlus /> {t("invitation.register_button")}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="invitation-step step-2">
                            <h1>{t("invitation.step2_title")}</h1>
                            <form
                                onSubmit={handleFormSubmit}
                                className="invitation-form"
                            >
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleInputChange}
                                        placeholder={t(
                                            "invitation.firstName_label"
                                        )}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleInputChange}
                                        placeholder={t("invitation.lastName_label")}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder={t("invitation.email_label")}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="country"
                                        value={formData.country}
                                        onClick={() => setShowCountryModal(true)}
                                        placeholder={t("invitation.country_label")}
                                        readOnly
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="city"
                                        value={formData.city}
                                        onClick={() => setShowCityModal(true)}
                                        placeholder={t("invitation.city_label")}
                                        readOnly
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <input
                                        type="tel"
                                        name="phoneNumber"
                                        value={formData.phoneNumber}
                                        onChange={handleInputChange}
                                        placeholder={t(
                                            "invitation.phoneNumber_label"
                                        )}
                                    />
                                </div>

                                {/* Conditional rendering for session selection */}
                                {formData.countryId && formData.cityId ? (
                                    <>
                                        <h3 className="session-title">
                                            {t("invitation.session_choice_title")}
                                        </h3>
                                        <div className="session-options">
                                            {sessions
                                                .filter((session) => {
                                                    const now = new Date();
                                                    // Filter by cityId AND (session is ongoing or in the future)
                                                    return (
                                                        session.city_id ===
                                                        formData.cityId &&
                                                        session.end_time &&
                                                        new Date(session.end_time) >
                                                        now
                                                    );
                                                })
                                                .map((session) => (
                                                    <div
                                                        key={session.id}
                                                        className="session-item"
                                                    >
                                                        <label className="session-main-label">
                                                            <input
                                                                type="checkbox"
                                                                name="session"
                                                                value={session.id}
                                                                onChange={
                                                                    handleSessionChange
                                                                }
                                                                checked={formData.session.includes(
                                                                    session.id
                                                                )}
                                                            />
                                                            <div className="session-label-content">
                                                                {" "}
                                                                {/* New div for better structuring */}
                                                                <p>
                                                                    <FaTag />{" "}
                                                                    <strong>
                                                                        {
                                                                            session.title
                                                                        }
                                                                    </strong>
                                                                </p>
                                                                <p>
                                                                    <FaLanguage />{" "}
                                                                    {
                                                                        session.language
                                                                    }
                                                                </p>
                                                                <p>
                                                                    <FaMapMarkerAlt />{" "}
                                                                    {
                                                                        session.address
                                                                    }
                                                                </p>
                                                                <p>
                                                                    <FaCalendarAlt />{" "}
                                                                    {new Date(
                                                                        session.start_time
                                                                    ).toLocaleDateString(
                                                                        "fr-FR",
                                                                        {
                                                                            month: "long",
                                                                            day: "numeric",
                                                                            year: "numeric",
                                                                        }
                                                                    )}
                                                                </p>
                                                                <p>
                                                                    <FaClock />{" "}
                                                                    {new Date(
                                                                        session.start_time
                                                                    ).toLocaleTimeString(
                                                                        "fr-FR",
                                                                        {
                                                                            hour: "2-digit",
                                                                            minute: "2-digit",
                                                                        }
                                                                    )}{" "}
                                                                    -{" "}
                                                                    {new Date(
                                                                        session.end_time
                                                                    ).toLocaleTimeString(
                                                                        "fr-FR",
                                                                        {
                                                                            hour: "2-digit",
                                                                            minute: "2-digit",
                                                                        }
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </label>
                                                    </div>
                                                ))}
                                        </div>
                                    </>
                                ) : (
                                    <p>
                                        {t(
                                            "invitation.select_country_city_for_sessions"
                                        )}
                                    </p> // New translation key
                                )}
                                <button type="submit" className="invitation-button">
                                    {t("invitation.submit_form_button")}
                                </button>
                            </form>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="invitation-step step-3">
                            <h1>{t("invitation.step3_title")}</h1>
                            <p className="invitation-text-summary">
                                {t("invitation.step3_summary")}
                            </p>
                            <div className="invitation-video-player-area">
                                <div className="invitation-player-wrapper">
                                    <div className="video-overlay"></div>
                                    <div
                                        id="invitation-youtube-player-confirm"
                                        ref={iframeRefStep3}
                                    ></div>
                                </div>
                                <p className="invitation-video-instruction">
                                    {t("invitation.click_to_play")}
                                </p>
                                <div className="video-controls">
                                    <button
                                        onClick={handleRewind}
                                        className="video-control-button"
                                    >
                                        <FaBackward />
                                    </button>
                                    <button
                                        onClick={handlePlayPause}
                                        className="play-pause-button"
                                    >
                                        {playing ? <FaPause /> : <FaPlay />}
                                    </button>
                                    <button
                                        onClick={handleFastForward}
                                        className="video-control-button"
                                    >
                                        <FaForward />
                                    </button>
                                </div>
                            </div>
                            <div className="button-center-container">
                                <button
                                    onClick={handleConfirmPresence}
                                    className="invitation-button"
                                >
                                    <FaCheckCircle />{" "}
                                    {t("invitation.confirm_presence_button")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {showCountryModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h2>{t("invitation.select_country")}</h2>
                            <button
                                onClick={() => setShowCountryModal(false)}
                                className="modal-close-button"
                            >
                                <FaTimes />
                            </button>
                            <div className="modal-options">
                                {countries.map((country) => (
                                    <button
                                        key={country.id}
                                        onClick={() =>
                                            handleCountrySelect(
                                                country.id,
                                                i18n.language === "fr"
                                                    ? country.name_fr
                                                    : country.name_en
                                            )
                                        }
                                        className="modal-option-button"
                                    >
                                        {i18n.language === "fr"
                                            ? country.name_fr
                                            : country.name_en}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {showCityModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h2>{t("invitation.select_city")}</h2>
                            <button
                                onClick={() => setShowCityModal(false)}
                                className="modal-close-button"
                            >
                                <FaTimes />
                            </button>
                            <div className="modal-options">
                                {cities.map((city) => (
                                    <button
                                        key={city.id}
                                        onClick={() =>
                                            handleCitySelect(
                                                city.id,
                                                i18n.language === "fr"
                                                    ? city.name_fr
                                                    : city.name_en
                                            )
                                        }
                                        className="modal-option-button"
                                    >
                                        {i18n.language === "fr"
                                            ? city.name_fr
                                            : city.name_en}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {showErrorModal && (
                    <div className="modal-overlay">
                        <div className="modal-content modal-error">
                            <h2>{t("invitation.error_modal_title")}</h2>
                            <p>{errorMessage}</p>
                            <button
                                onClick={() => setShowErrorModal(false)}
                                className="invitation-button"
                            >
                                {t("invitation.close_button")}
                            </button>
                        </div>
                    </div>
                )}
                {showSuccessModal && (
                    <div className="modal-overlay">
                        <div className="modal-content modal-success">
                            <h2>{t("invitation.success_modal_title")}</h2>
                            <p>{t("invitation.success_modal_message")}</p>
                            <button
                                onClick={handleSuccessModalClose}
                                className="invitation-button"
                            >
                                {t("invitation.close_button")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
}

export default Invitation;